/**
 * @file dashboardPreferences.js
 * @description Preferencias del Dashboard inicial (localStorage).
 *   Namespace: archat.dashboard.preferences — temporal hasta API de preferencias unificada.
 *
 * @typedef {Object} DashboardBlockPrefs
 * @property {boolean} welcome
 * @property {boolean} promotions
 * @property {boolean} usage
 * @property {boolean} sessions
 * @property {boolean} files
 * @property {boolean} recentConversations — último mensaje por contacto en el panel
 *
 * @typedef {Object} DashboardPreferences
 * @property {boolean} showOnStart
 * @property {boolean} [showOnStartExplicit] — true si el usuario tocó el interruptor en Ajustes (no es el default legacy auto-guardado).
 * @property {DashboardBlockPrefs} blocks
 */

export const DASHBOARD_PREFS_STORAGE_KEY = 'archat.dashboard.preferences';

/**
 * @param {DashboardPreferences} merged
 * @returns {DashboardPreferences}
 */
function migrateLegacyShowOnStart(merged) {
  try {
    // Antes el default persistido era showOnStart=true sin opt-in; ahora el default es Contactos salvo preferencia explícita.
    if (merged.showOnStart === true && merged.showOnStartExplicit !== true) {
      return { ...merged, showOnStart: false };
    }
  } catch {
    /* noop */
  }
  return merged;
}

/**
 * @returns {DashboardPreferences}
 */
export function getDefaultDashboardPreferences() {
  return {
    showOnStart: false,
    showOnStartExplicit: false,
    blocks: {
      welcome: true,
      usage: true,
      files: true,
      promotions: true,
      sessions: true,
      recentConversations: true,
    },
  };
}

/**
 * @returns {DashboardPreferences}
 */
export function loadDashboardPreferences() {
  try {
    if (typeof localStorage === 'undefined') return getDefaultDashboardPreferences();
    const raw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
    if (!raw) {
      const def = getDefaultDashboardPreferences();
      try {
        localStorage.setItem(DASHBOARD_PREFS_STORAGE_KEY, JSON.stringify(def));
      } catch {
        /* noop: storage lleno o privado */
      }
      return def;
    }
    const parsed = JSON.parse(raw);
    const def = getDefaultDashboardPreferences();
    let merged = {
      ...def,
      ...parsed,
      blocks: {
        ...def.blocks,
        ...(parsed.blocks && typeof parsed.blocks === 'object' ? parsed.blocks : {}),
      },
    };
    merged = migrateLegacyShowOnStart(merged);
    if (merged.showOnStart !== parsed.showOnStart) {
      try {
        localStorage.setItem(DASHBOARD_PREFS_STORAGE_KEY, JSON.stringify(merged));
      } catch {
        /* noop */
      }
    }
    return merged;
  } catch {
    return getDefaultDashboardPreferences();
  }
}

/**
 * @param {Partial<DashboardPreferences>} partial
 */
export function saveDashboardPreferences(partial) {
  const def = getDefaultDashboardPreferences();
  let current = def;
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(DASHBOARD_PREFS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        current = {
          ...def,
          ...parsed,
          blocks: {
            ...def.blocks,
            ...(parsed.blocks && typeof parsed.blocks === 'object' ? parsed.blocks : {}),
          },
        };
      }
    }
  } catch {
    current = def;
  }
  const next = {
    ...current,
    ...partial,
    blocks: {
      ...current.blocks,
      ...(partial.blocks && typeof partial.blocks === 'object' ? partial.blocks : {}),
    },
  };
  if (Object.prototype.hasOwnProperty.call(partial, 'showOnStart')) {
    next.showOnStartExplicit = true;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DASHBOARD_PREFS_STORAGE_KEY, JSON.stringify(next));
    }
  } catch {
    /* noop */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('archat:dashboard-prefs-changed', { detail: next }));
  }
  return next;
}

/**
 * @returns {'dashboard'|'contacts'}
 */
export function getInitialAppViewFromPrefs() {
  try {
    const p = loadDashboardPreferences();
    if (p.showOnStart === true) return 'dashboard';
  } catch {
    /* noop */
  }
  return 'contacts';
}
